import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "../lib/api";
import { Loader2, Siren, Plus, Camera, FileText, ShieldCheck } from "lucide-react";
import dayjs from "dayjs";

const reportCountLabel = (count) => {
  if (count === 0) return "No accident reports filed.";
  const plural = count > 1 ? "s" : "";
  return `${count} report${plural} filed.`;
};

const FAULT_LABELS = {
  third_party_at_fault: "Third party at fault",
  renter_at_fault: "I was at fault",
  shared: "Shared fault",
  unknown: "Unknown / hit and run",
};

export const AccidentsPanel = () => {
  const navigate = useNavigate();
  const [reports, setReports] = useState(null);

  useEffect(() => {
    api.get("/accidents").then((r) => setReports(r.data)).catch(() => setReports([]));
  }, []);

  if (reports === null)
    return <div className="flex justify-center py-16"><Loader2 className="w-7 h-7 animate-spin text-[#009900]" /></div>;

  return (
    <div data-testid="accidents-panel">
      <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-5 flex items-start gap-3" data-testid="accident-emergency-banner">
        <Siren className="w-5 h-5 text-red-600 mt-0.5 shrink-0" />
        <div className="text-sm text-red-800">
          <strong>In an emergency call 999.</strong> For roadside assistance call <strong>800 EUROPCAR (800 38767227)</strong> — one tap away, 24/7. Never admit fault at the scene and take photos before vehicles are moved.
        </div>
      </div>

      <div className="flex items-center justify-between mb-5 flex-wrap gap-3">
        <p className="text-sm text-[#57685e]">
          {reportCountLabel(reports.length)}
        </p>
        <button onClick={() => navigate("/report-accident")} className="btn-cta rounded px-5 py-2.5 text-xs flex items-center gap-2" data-testid="new-accident-btn">
          <Plus className="w-4 h-4" /> Report an accident
        </button>
      </div>

      <div className="space-y-4">
        {reports.map((r) => (
          <div key={r.report_id} className="bg-white border border-[#e2ebe4] rounded-lg p-4" data-testid={`accident-row-${r.report_id}`}>
            <div className="flex flex-wrap items-center gap-3">
              <span className="font-mono font-bold text-[#144733]">{r.report_id}</span>
              <span className="text-[10px] uppercase tracking-wide bg-amber-50 text-amber-700 border border-amber-200 rounded-full px-2.5 py-1 font-semibold" data-testid="accident-status-badge">
                Submitted — under review
              </span>
              <span className="text-xs text-[#57685e]">{r.vehicle} · {r.booking_ref}</span>
              <span className="ml-auto text-xs text-[#8a978f]">{dayjs(r.created_at).format("DD MMM YYYY, HH:mm")}</span>
            </div>
            <div className="mt-2 text-sm text-[#0F1E17]">{r.location} — {r.description}</div>
            <div className="mt-2 flex flex-wrap gap-3 text-xs text-[#57685e]">
              <span>Fault: <strong>{FAULT_LABELS[r.fault] || r.fault}</strong></span>
              <span>Damage: <strong>{r.damage_place}</strong></span>
              {r.police?.involved && <span className="flex items-center gap-1"><FileText className="w-3.5 h-3.5" /> Police report {r.police.report_no || "pending"}</span>}
              {r.third_party?.involved && <span>Third party involved{r.third_party.injuries ? " · injuries reported" : ""}</span>}
              <span className="flex items-center gap-1"><Camera className="w-3.5 h-3.5" /> {r.attachments.length} attachment{r.attachments.length !== 1 ? "s" : ""}</span>
            </div>
            {r.remarks && <p className="mt-2 text-xs text-[#57685e] italic">Remarks: {r.remarks}</p>}
          </div>
        ))}
      </div>

      <div className="mt-6 bg-[#f4f7f4] border border-[#e2ebe4] rounded-lg p-4 text-xs text-[#144733] flex items-start gap-2" data-testid="accident-policy-box">
        <ShieldCheck className="w-4 h-4 mt-0.5 shrink-0 text-[#009900]" />
        <p>
          Standard insurance covers vehicle body damage not caused by you, provided a <strong>valid final police report</strong> is submitted (a reference number is not sufficient). With <strong>SCDW (Full Protection)</strong> you have zero-liability cover for body damage regardless of fault, with a valid final report. A replacement vehicle is arranged after final-report validation.
        </p>
      </div>
    </div>
  );
};
